import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Share } from "lucide-react";

const DISMISS_KEY = "a2hs_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [mode, setMode] = useState(null); // 'android' | 'ios' | null
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      /* noop */
    }
    if (dismissed || isStandalone()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode("android");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari has no beforeinstallprompt — show a gentle hint instead
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    if (isIOS) {
      const t = setTimeout(() => setMode((m) => m || "ios"), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* noop */
    }
    setMode(null);
  };

  useEffect(() => {
    if (mode) document.body.classList.add("has-install-banner");
    else document.body.classList.remove("has-install-banner");
    return () => document.body.classList.remove("has-install-banner");
  }, [mode]);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => {});
    dismiss();
  };

  return (
    <AnimatePresence>
      {mode && (
        <motion.div
          className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          data-testid="install-prompt"
        >
          <div className="w-full max-w-md glass rounded-lg p-4 flex items-center gap-3 shadow-[0_16px_40px_-12px_rgba(20,40,45,0.5)]">
            <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-lg shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg leading-tight">Keep the fridge handy</p>
              {mode === "ios" ? (
                <p className="text-xs text-[#4a5c62] mt-0.5 flex items-center gap-1 flex-wrap">
                  Tap <Share className="w-3.5 h-3.5 inline" strokeWidth={1.5} /> then "Add to Home Screen"
                </p>
              ) : (
                <p className="text-xs text-[#4a5c62] mt-0.5">Install it for one-tap fridge investigations.</p>
              )}
            </div>
            {mode === "android" && (
              <button
                data-testid="install-accept-btn"
                onClick={install}
                className="btn-ink rounded-sm px-4 py-2.5 text-sm flex items-center gap-1.5 shrink-0"
              >
                <Download className="w-4 h-4" strokeWidth={1.5} /> Install
              </button>
            )}
            <button
              data-testid="install-dismiss-btn"
              onClick={dismiss}
              className="w-8 h-8 rounded-full bg-white/60 hairline flex items-center justify-center shrink-0"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
